<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  settingItem: { type: Object, default: null },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const store = useStore();
const inboxesList = useMapGetter('inboxes/getInboxes');

onMounted(() => {
  store.dispatch('inboxes/get');
});

const form = ref({
  form_name: '',
  form_id: '',
  advertiser_id: '',
  inbox_id: null,
  active: true,
});

const isEditing = ref(false);

watch(
  () => props.show,
  val => {
    if (val && props.settingItem) {
      isEditing.value = true;
      form.value = {
        form_name: props.settingItem.form_name || '',
        form_id: props.settingItem.form_id || '',
        advertiser_id: props.settingItem.advertiser_id || '',
        inbox_id: props.settingItem.inbox_id || null,
        active: props.settingItem.active !== false,
      };
    } else if (val) {
      isEditing.value = false;
      form.value = {
        form_name: '',
        form_id: '',
        advertiser_id: '',
        inbox_id: inboxesList.value?.[0]?.id || null,
        active: true,
      };
    }
  }
);

const handleSubmit = () => {
  if (!form.value.form_id || !form.value.advertiser_id || !form.value.inbox_id) {
    return;
  }

  const payload = {
    lead_setting: {
      form_name: form.value.form_name,
      form_id: form.value.form_id.trim(),
      advertiser_id: form.value.advertiser_id.trim(),
      inbox_id: parseInt(form.value.inbox_id, 10),
      active: form.value.active,
    },
  };

  if (isEditing.value && props.settingItem) {
    payload.id = props.settingItem.id;
  }

  emit('save', payload);
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-5 p-4"
      @click.self="emit('close')"
    >
      <div
        class="bg-n-solid-1 rounded-xl shadow-xl w-full max-w-lg overflow-y-auto border border-n-weak max-h-[90vh]"
      >
        <div class="flex items-center justify-between p-6 border-b border-n-weak">
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{
              isEditing
                ? t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.EDIT_TITLE')
                : t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.CREATE_TITLE')
            }}
          </h3>
          <button
            class="text-n-slate-11 hover:text-n-slate-12 text-lg"
            @click="emit('close')"
          >
            ✕
          </button>
        </div>

        <form class="p-6 flex flex-col gap-4" @submit.prevent="handleSubmit">
          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.FORM_NAME') }}
            </label>
            <input
              v-model="form.form_name"
              type="text"
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-b-500"
              :placeholder="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.FORM_NAME_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.FORM_ID') }} <span class="text-r-500">*</span>
            </label>
            <input
              v-model="form.form_id"
              type="text"
              required
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-b-500 font-mono"
              :placeholder="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.FORM_ID_PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-11 mt-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.FORM_ID_HELP') }}
            </p>
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.ADVERTISER_ID') }} <span class="text-r-500">*</span>
            </label>
            <input
              v-model="form.advertiser_id"
              type="text"
              required
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-b-500 font-mono"
              :placeholder="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.ADVERTISER_ID_PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-11 mt-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.ADVERTISER_ID_HELP') }}
            </p>
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.TARGET_INBOX') }} <span class="text-r-500">*</span>
            </label>
            <select
              v-model="form.inbox_id"
              required
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-md bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-b-500"
            >
              <option :value="null" disabled>
                {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.SELECT_INBOX_PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in inboxesList"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }} ({{ inbox.channel_type }})
              </option>
            </select>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.TARGET_INBOX_HELP') }}
            </p>
          </div>

          <div class="flex items-center gap-2 pt-2">
            <input
              id="activeCheckbox"
              v-model="form.active"
              type="checkbox"
              class="rounded border-n-weak text-b-500 focus:ring-b-500"
            />
            <label for="activeCheckbox" class="text-sm text-n-slate-12 select-none cursor-pointer">
              {{ t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.ACTIVE_LABEL') }}
            </label>
          </div>

          <div class="flex justify-end gap-3 mt-4 pt-4 border-t border-n-weak">
            <Button
              faded
              slate
              type="button"
              :label="t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.CANCEL')"
              @click="emit('close')"
            />
            <Button
              blue
              type="submit"
              :label="isEditing ? t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.UPDATE') : t('INTEGRATION_SETTINGS.TIKTOK_LEAD_ADS.MODAL.CREATE')"
            />
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
